import { html, LitElement, nothing, TemplateResult } from 'lit'
import { property, state } from 'lit/decorators.js'
import { Attribute, AttributeValue } from './product-types.js'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'

export interface VariantOption {
  key: string,
  value: number
}

export type VariantFilterStrategyCallback = (option: VariantOption) => void

export type DisplayTypeContext = {
  attribute: Attribute,
  selectOption: VariantFilterStrategyCallback,
  chooseFilterText: string,
  displayLimit: number,
  filterContainsDropdown: boolean,
}

export type DisplayType = (context: DisplayTypeContext) => TemplateResult

export class VariantFilterBase extends LitElement {
  @property({ attribute: false })
  attribute: Attribute = { id: 0, name: '', options: [] }

  @property({ attribute: false })
  callback: VariantFilterStrategyCallback = () => undefined
}

export class VariantFilterBaseExtended extends VariantFilterBase {
  @property({ attribute: false })
  displayLimit: number = Infinity

  @property({ attribute: false })
  filterContainsDropdown: boolean = false

  @state()
  protected expanded = false

  protected get visibleOptions(): AttributeValue[] {
    return this.expanded
      ? this.attribute.options
      : this.attribute.options.slice(0, this.displayLimit)
  }

  protected get selectedOptionName(): string {
    return this.attribute.options.find(option => option.selected)?.name ?? ''
  }

  protected showAllOptions = () => {
    this.expanded = true
  }

  protected renderShowMoreLink() {
    const hiddenCount = this.attribute.options.length - this.visibleOptions.length

    if (this.expanded || hiddenCount <= 0) {
      return nothing
    }

    return html`
      <cse-link @click="${this.showAllOptions}">
        +${hiddenCount}
      </cse-link>
    `
  }

  createVariantOption(id: string, value: number): VariantOption {
    return {
      key: id,
      value: value,
    }
  }

  shouldBeCrossedOut(option: AttributeValue): boolean {
    return option.orderable === false && !this.filterContainsDropdown
  }

  handleSwatchClick = (option: AttributeValue) => {
    if (!option.selected) {
      this.callback(
        this.createVariantOption(this.attribute.id.toString(), option.id)
      )
    }
  }

  handleToolTipMessage(option: AttributeValue): string | TranslateResult {
    if (this.filterContainsDropdown) {
      return option.name
    }

    if (option.exists === false) {
      return t('combination_not_available', { ns: 'csc-variant-filter', default: 'This combination is not available.' })
    }

    if (option.orderable === false) {
      return t('not_orderable', { ns: 'csc-variant-filter', default: 'This product cannot be ordered.' })
    }

    return option.name
  }
}
